import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { ProductSelectEvent, StandardEvents } from '@shopify/events';

/**
 * The 3-rung cart tier ladder: free shipping, then a $ off tier, then a
 * bigger $ off tier. Reads the live cart total via the Cart Ajax API (same
 * event av-shipping-bar uses) and re-renders on every cart change. Tier
 * thresholds/amounts come from theme settings and must match the real
 * automatic discounts configured in Shopify -- this component never
 * invents a number, it only reflects settings back as copy and a progress
 * fill.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [message]
 * @property {HTMLElement} [track]
 * @property {HTMLElement} [fill]
 *
 * @extends {Component<Refs>}
 */
class AvTierLadder extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ProductSelectEvent.eventName, this.#handleVariantUpdate);
    this.#cartSubtotalCents = Number(this.dataset.initialTotalCents ?? 0);
    this.#selectedVariantId = String(this.dataset.selectedVariantId ?? '');
    this.#cartVariantIds = new Set(JSON.parse(this.dataset.cartVariantIds ?? '[]').map(String));
    this.#render(this.#totalCents());
    // The cart drawer re-renders through morph, which resets the message and
    // fill to their empty server markup on this live element without
    // reconnecting it (morph calls updatedCallback on the incoming copy, not
    // this one). Redraw whenever that happens; #render only writes on change,
    // so its own writes settle after one pass.
    this.#morphObserver = new MutationObserver(() => this.#render(this.#totalCents()));
    this.#morphObserver.observe(this, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['data-initial-total-cents', 'style'] });
  }

  #morphObserver;
  #cartSubtotalCents = 0;
  #selectedVariantId = '';
  #cartVariantIds = new Set();

  #totalCents() {
    return this.#cartSubtotalCents + (this.classList.contains('av-tier-ladder--slim') && !this.#cartVariantIds.has(this.#selectedVariantId) ? Number(this.dataset.selectedVariantCents ?? 0) : 0);
  }

  #handleVariantUpdate = (event) => {
    const variantId = String(event.detail?.detail?.variantId ?? event.detail?.data?.variantId ?? event.detail?.variantId ?? '');
    this.#selectedVariantId = variantId;
    const ids = JSON.parse(this.dataset.variantIds ?? '[]').map(String);
    const prices = JSON.parse(this.dataset.variantPrices ?? '[]');
    const index = ids.indexOf(variantId);
    if (index < 0) return;
    this.dataset.selectedVariantCents = String(prices[index] ?? 0);
    this.#render(this.#totalCents());
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ProductSelectEvent.eventName, this.#handleVariantUpdate);
    this.#morphObserver?.disconnect();
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        if (!cart) return;
        // Thresholds compare against the pre-discount items subtotal, never
        // the discounted total -- sum the lines' own costs rather than
        // reading cart.cost.totalAmount (which reflects cart-level discounts).
        const subtotalCents = cart.lines.reduce(
          (sum, line) => sum + Math.round(Number(line.cost.totalAmount.amount) * 100),
          0
        );
        this.#cartSubtotalCents = subtotalCents;
        this.dataset.initialTotalCents = String(subtotalCents);
        this.#cartVariantIds = new Set(cart.lines.map((line) => String(line.id).split(':')[0]));
        this.#render(this.#totalCents());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[av-tier-ladder] Event promise rejected:', error);
      });
  };

  #render(totalCents) {
    const { message, track, fill } = this.refs;
    if (!message) return;

    const currency = this.dataset.currency ?? 'USD';
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const money = (cents) => formatMoney(cents, moneyFormat, currency);

    const showFreeShipping = this.dataset.showFreeShipping === 'true';
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const tier1 = Math.round(Number(this.dataset.tier1Cents ?? 0) * rate);
    const tier2 = Math.round(Number(this.dataset.tier2Cents ?? 0) * rate);
    const tier2Discount = Math.round(Number(this.dataset.tier2DiscountCents ?? 0) * rate);
    const tier3 = Math.round(Number(this.dataset.tier3Cents ?? 0) * rate);
    const tier3Discount = Math.round(Number(this.dataset.tier3DiscountCents ?? 0) * rate);

    // Past the free shipping rung, say so before pointing at the next tier:
    // a $66 cart used to read only "Add $34 for $15 off" (checkout review 2026-10-05).
    const unlocked = showFreeShipping && totalCents >= tier1 ? 'Free US shipping unlocked. ' : '';
    let text;
    if (this.classList.contains('av-tier-ladder--slim') && Number(this.dataset.selectedVariantCents ?? 0) >= tier1) {
      text = this.dataset.variantAloneFreeText ?? '';
    } else if (showFreeShipping && totalCents < tier1) {
      text = this.#fill(this.dataset.shippingText, { amount: money(tier1 - totalCents) });
    } else if (totalCents < tier2) {
      text = unlocked + this.#fill(this.dataset.tier2Text, { amount: money(tier2 - totalCents), discount: money(tier2Discount) });
    } else if (totalCents < tier3) {
      text = unlocked + this.#fill(this.dataset.tier3Text, { amount: money(tier3 - totalCents), discount: money(tier3Discount) });
    } else {
      text = this.#fill(this.dataset.achievedText, { discount: money(tier3Discount) });
    }
    if (message.textContent !== text) message.textContent = text;

    if (track && fill) {
      const percent = tier3 > 0 ? Math.min(100, (totalCents / tier3) * 100) : 100;
      if (fill.style.inlineSize !== `${percent}%`) fill.style.inlineSize = `${percent}%`;
      if (track.getAttribute('aria-valuenow') !== String(Math.round(percent))) track.setAttribute('aria-valuenow', String(Math.round(percent)));

      for (const marker of track.querySelectorAll('[data-tier-marker]')) {
        const markerThreshold = Math.round(Number(marker.getAttribute('data-tier-marker')) * rate);
        marker.classList.toggle('av-tier-ladder__marker--reached', totalCents >= markerThreshold);
      }
    }
  }

  /**
   * @param {string} template
   * @param {Record<string, string>} tokens
   */
  #fill(template, tokens) {
    let result = template ?? '';
    for (const [key, value] of Object.entries(tokens)) {
      result = result.replaceAll(`{{ ${key} }}`, value).replaceAll(`{{${key}}}`, value);
    }
    return result;
  }
}

if (!customElements.get('av-tier-ladder')) {
  customElements.define('av-tier-ladder', AvTierLadder);
}
